/** Coordenadas grandes no ficheiro IFC: geometria longe da origem.
 *
 *  Modelar em coordenadas de mapa (PT-TM06, UTM: dezenas ou centenas de
 *  km da origem) é má prática em qualquer versão do IFC, não um método
 *  antigo aceitável: perde-se precisão na geometria e no desenho, e a
 *  georreferenciação fica escondida nas posições em vez de declarada. O
 *  certo é modelar perto da origem do ficheiro (assinalada pelo marcador
 *  de coordenação) e declarar a deslocação para o mapa na
 *  IfcMapConversion (IFC4 e seguintes) ou no
 *  ePSet_MapConversion (IFC2X3). Pedido do Rui, 16/10/2026, a propósito
 *  do Cha139 (quatro modelos com as coordenadas PT-TM06 nas posições). */

/** A partir daqui avisa-se: uma obra raramente passa de 1 km, e as
 *  coordenadas de mapa estão a dezenas de km da origem ou mais. */
export const LIMIAR_COORDENADAS_GRANDES_M = 10_000

/** A distância (m) da geometria à origem do ficheiro, a partir da
 *  translação com que o conversor a recentrou ([x, y, z], Y para cima). */
export function distanciaAOrigem(recentre: readonly number[]): number {
  const [x = 0, y = 0, z = 0] = recentre
  return Math.hypot(x, y, z)
}

export function temCoordenadasGrandes(distanciaM: number): boolean {
  return Number.isFinite(distanciaM) && distanciaM > LIMIAR_COORDENADAS_GRANDES_M
}

/** "164 km", "12,5 km". */
export function distanciaParaLer(m: number): string {
  const km = m / 1000
  return km >= 100 ? `${Math.round(km)} km` : `${km.toFixed(1).replace('.', ',')} km`
}

/** O aviso, para a notificação ao abrir e para o painel do modelo. */
export function avisoDeCoordenadasGrandes(nomeDoModelo: string, distanciaM: number): string {
  return (
    `${nomeDoModelo}: a geometria está a ${distanciaParaLer(distanciaM)} da origem do ficheiro `
    + '(coordenadas de mapa nas posições dos elementos). É má prática em qualquer versão do IFC: '
    + 'modele perto da origem do ficheiro (assinalada pelo marcador de coordenação) e declare a deslocação para o mapa na '
    + 'IfcMapConversion (ou no ePSet_MapConversion, em IFC2X3).'
  )
}

/** Vários modelos abertos de uma vez (uma sessão): um só aviso. */
export function avisoJuntoDeCoordenadasGrandes(modelos: readonly { nome: string; distanciaM: number }[]): string {
  if (modelos.length === 1) return avisoDeCoordenadasGrandes(modelos[0].nome, modelos[0].distanciaM)
  const max = Math.max(...modelos.map((m) => m.distanciaM))
  return (
    `${modelos.length} modelos têm a geometria longe da origem do ficheiro (até ${distanciaParaLer(max)}): `
    + `${modelos.map((m) => m.nome).join(', ')}. Coordenadas de mapa nas posições dos elementos são má prática `
    + 'em qualquer versão do IFC: modele perto da origem do ficheiro (assinalada pelo marcador de coordenação) e declare a deslocação '
    + 'para o mapa na IfcMapConversion (ou no ePSet_MapConversion, em IFC2X3).'
  )
}

let pendentes: { nome: string; distanciaM: number }[] = []
let temporizador: ReturnType<typeof setTimeout> | null = null

/** Junta os avisos dos modelos que abrem quase ao mesmo tempo e entrega
 *  um só, um segundo e meio depois do último. */
export function avisarCoordenadasGrandes(
  nome: string,
  distanciaM: number,
  entregar: (texto: string) => void,
  esperaMs = 1500,
): void {
  pendentes.push({ nome, distanciaM })
  if (temporizador) clearTimeout(temporizador)
  temporizador = setTimeout(() => {
    const lista = pendentes
    pendentes = []
    temporizador = null
    if (lista.length > 0) entregar(avisoJuntoDeCoordenadasGrandes(lista))
  }, esperaMs)
}
